// ── Adapters: map live API responses (snake_case, nullable) to the UI's ────────
// ── existing mock-shaped interfaces (camelCase, from ./data) ───────────────────

import type * as T from "./api/types";
import type {
  AdminTalent, AdminOrg, AdminPosting, AdminApplication, AdminPayment,
  VerificationItem, AdminReport, AdminAnnouncement, AuditEvent, AdminUser,
  OnboardingQuestion,
} from "./data";

// ── Formatting helpers ──────────────────────────────────────────────────────

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(d);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const datePart = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(d);
  const timePart = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  return `${datePart}, ${timePart}`;
}

export function formatMoney(n: number | null | undefined): string {
  const v = typeof n === "number" && !isNaN(n) ? n : 0;
  return `SAR ${Math.round(v).toLocaleString("en-US")}`;
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(iso);
}

const PALETTE = [
  "#7A1533", "#1f4e79", "#2d4a22", "#5c2a0a", "#4a1a5c", "#1a3a4a",
  "#6b3a00", "#1a1a1a", "#0a3d2e", "#3a1a5c", "#1f6b45", "#005a7c",
  "#7a3500", "#2a2a6e", "#8b0000", "#4a004a",
];

export function initialsOf(name: string | null | undefined): string {
  const n = (name ?? "").trim();
  if (!n) return "—";
  const parts = n.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

export function colorFor(seed: string | null | undefined): string {
  const s = seed ?? "x";
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

// ── Entity adapters ──────────────────────────────────────────────────────────

export function mapTalent(t: T.Talent): AdminTalent {
  return {
    id: t.id,
    name: t.name,
    initials: t.initials || initialsOf(t.name),
    color: t.color || colorFor(t.id),
    category: t.category ?? "",
    location: t.location ?? "",
    dayRate: t.day_rate ?? "—",
    verification: (t.verification_status as AdminTalent["verification"]) ?? "Unverified",
    account: (t.account_status as AdminTalent["account"]) ?? "Active",
    joinedDate: formatDate(t.joined_date),
    lastActive: relativeTime(t.last_active),
    totalApplications: t.total_applications ?? 0,
    totalHires: t.total_hires ?? 0,
    totalEarnings: formatMoney(t.total_earnings),
    walletBalance: formatMoney(t.wallet_balance),
    pendingPayout: formatMoney(t.pending_payout),
    rating: t.rating ?? 0,
    reviews: t.reviews ?? 0,
    email: t.email ?? "",
    phone: t.phone ?? "",
    flagged: !!t.flagged,
    bio: t.bio ?? "",
  };
}

export function mapOrg(o: T.Org): AdminOrg {
  return {
    id: o.id,
    name: o.name,
    initials: o.initials || initialsOf(o.name),
    color: o.color || colorFor(o.id),
    contact: o.contact_name ?? "—",
    contactRole: o.contact_role ?? "—",
    industry: o.industry ?? "",
    location: o.location ?? "",
    kyb: (o.kyb_status as AdminOrg["kyb"]) ?? "Unverified",
    account: (o.account_status as AdminOrg["account"]) ?? "Active",
    joinedDate: formatDate(o.joined_date),
    lastActive: relativeTime(o.last_active),
    activePostings: o.active_postings ?? 0,
    totalHires: o.total_hires ?? 0,
    totalSpend: formatMoney(o.total_spend),
    walletBalance: formatMoney(o.wallet_balance),
    escrowHeld: formatMoney(o.escrow_held),
    customFee: o.custom_fee === null || o.custom_fee === undefined ? undefined : o.custom_fee,
    rating: o.rating ?? 0,
    reviews: o.reviews ?? 0,
    email: o.email ?? "",
    phone: o.phone ?? "",
    crNumber: o.cr_number ?? "—",
    flagged: !!o.flagged,
  };
}

export function mapPosting(p: T.Posting, orgNameById?: Map<string, string>): AdminPosting {
  const joined = (p as unknown as { org_name?: string }).org_name;
  const orgName = joined ?? (p.org_id ? orgNameById?.get(p.org_id) : undefined) ?? "—";
  return {
    id: p.id,
    title: p.title,
    orgId: p.org_id ?? "",
    orgName,
    category: p.category ?? "",
    location: p.location ?? "",
    budget: p.budget ?? "—",
    status: (p.status as AdminPosting["status"]) ?? "Draft",
    posted: formatDate(p.posted_date),
    totalApplications: p.total_applications ?? 0,
    hired: p.hired_count ?? 0,
  };
}

export function mapApplication(a: T.Application): AdminApplication {
  return {
    id: a.id,
    talentId: a.talent_id ?? "",
    talentName: a.talent_name ?? "—",
    talentCategory: a.talent_category ?? "—",
    postingId: a.posting_id ?? "",
    postingTitle: a.posting_title ?? "—",
    orgId: a.org_id ?? "",
    orgName: a.org_name ?? "—",
    proposedRate: a.proposed_rate ?? "—",
    status: (a.status as AdminApplication["status"]) ?? "Applied",
    appliedDate: formatDate(a.applied_date),
    updatedDate: formatDate(a.updated_date),
  };
}

export function mapPayment(p: T.Payment): AdminPayment {
  return {
    id: p.id,
    talentId: p.talent_id ?? "",
    talentName: p.talent_name ?? "—",
    orgId: p.org_id ?? "",
    orgName: p.org_name ?? "—",
    postingTitle: p.posting_title ?? "—",
    amount: formatMoney(p.amount),
    platformFee: formatMoney(p.platform_fee),
    netToTalent: formatMoney(p.net_to_talent),
    status: (p.status as AdminPayment["status"]) ?? "Pending",
    date: formatDate(p.date),
    txnRef: p.txn_ref ?? "—",
  };
}

export function mapVerificationItem(v: T.VerificationItem): VerificationItem {
  return {
    id: v.id,
    entityType: (v.entity_type as VerificationItem["entityType"]) ?? "Talent",
    entityId: v.entity_id ?? "",
    entityName: v.entity_name,
    category: v.category ?? undefined,
    submittedDate: formatDate(v.submitted_date),
    documents: v.documents ?? [],
    priority: (v.priority as VerificationItem["priority"]) ?? "Normal",
    assignedTo: v.assigned_to ?? undefined,
    notes: v.notes ?? undefined,
    checkState: (v.check_state as VerificationItem["checkState"]) ?? undefined,
    checkedAt: v.checked_at ? formatDateTime(v.checked_at) : undefined,
    checkNotes: v.check_notes ?? undefined,
    details: v.details ?? [],
  };
}

export function mapReport(r: T.Report): AdminReport {
  return {
    id: r.id,
    reportedEntityType: (r.reported_entity_type as AdminReport["reportedEntityType"]) ?? "Talent",
    reportedEntityId: r.reported_entity_id ?? "",
    reportedEntityName: r.reported_entity_name ?? "—",
    reportedBy: r.reported_by ?? "—",
    reason: (r.reason as AdminReport["reason"]) ?? "Other",
    description: r.description ?? "",
    status: (r.status as AdminReport["status"]) ?? "Open",
    submittedDate: formatDate(r.submitted_date),
    severity: (r.severity as AdminReport["severity"]) ?? "Medium",
    assignedTo: r.assigned_to ?? undefined,
    resolutionNote: r.resolution_note ?? undefined,
  };
}

export function mapAnnouncement(a: T.Announcement): AdminAnnouncement {
  return {
    id: a.id,
    title: a.title,
    body: a.body ?? "",
    audience: (a.audience as AdminAnnouncement["audience"]) ?? "All",
    status: (a.status as AdminAnnouncement["status"]) ?? "Draft",
    createdBy: a.created_by ?? "—",
    createdDate: formatDate(a.created_date),
    sentDate: a.sent_date ? formatDate(a.sent_date) : undefined,
    scheduledDate: a.scheduled_date ? formatDate(a.scheduled_date) : undefined,
    recipients: a.recipients ?? 0,
  };
}

export function mapAuditEvent(e: T.AuditEvent): AuditEvent {
  return {
    id: e.id,
    timestamp: formatDateTime(e.timestamp),
    admin: e.admin ?? "—",
    action: e.action ?? "",
    entityType: e.entity_type ?? "",
    entityName: e.entity_name ?? "",
    entityId: e.entity_id ?? "",
    previousState: e.previous_state ?? undefined,
    newState: e.new_state ?? undefined,
    result: (e.result as AuditEvent["result"]) ?? "Success",
    ip: e.ip ?? "—",
  };
}

export function mapAdminUser(a: T.Admin): AdminUser {
  return {
    id: a.id,
    name: a.name,
    initials: initialsOf(a.name),
    email: a.email,
    role: (a.role as AdminUser["role"]) ?? "Support",
    status: (a.status as AdminUser["status"]) ?? "Active",
    lastLogin: a.last_login ? formatDateTime(a.last_login) : "Never",
    joinedDate: formatDate(a.joined_date),
    permissions: a.permissions ?? [],
  };
}

export interface MappedCategory {
  id: string;
  name: string;
  slug: string;
  talentCount: number;
  activePostings: number;
  active: boolean;
}

export function mapCategory(c: T.Category): MappedCategory {
  return {
    id: c.id,
    name: c.name,
    slug: c.slug,
    talentCount: c.talent_count ?? 0,
    activePostings: c.active_postings ?? 0,
    active: !!c.active,
  };
}

/** The category list carries its onboarding questions; this is that half of it. */
export function mapCategoryOnboarding(c: T.Category): { categoryId: string; questions: OnboardingQuestion[] } {
  return {
    categoryId: c.id,
    questions: (c.questions ?? []).map((q) => ({
      id: q.id,
      label: q.label,
      type: q.type as OnboardingQuestion["type"],
      required: !!q.required,
      options: q.options ?? undefined,
      placeholder: q.placeholder ?? undefined,
      hint: q.hint ?? undefined,
    })),
  };
}

export interface MappedNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  timestamp: string;
  read: boolean;
  entityId?: string;
  entityType?: string;
  actionLabel?: string;
  actionTarget?: string;
}

export function mapNotification(n: T.AdminNotification): MappedNotification {
  return {
    id: n.id,
    type: n.type ?? "system",
    title: n.title ?? "",
    body: n.body ?? "",
    timestamp: relativeTime(n.timestamp),
    read: !!n.read,
    entityId: n.entity_id ?? undefined,
    entityType: n.entity_type ?? undefined,
    actionLabel: n.action_label ?? undefined,
    actionTarget: n.action_target ?? undefined,
  };
}

export interface MappedPlatformStats {
  totalTalents: number;
  totalOrgs: number;
  activePostings: number;
  totalApplications: number;
  totalPaymentsVolume: string;
  verificationPending: number;
  reportsOpen: number;
  newTalents30d: number;
  newOrgs30d: number;
  hireRate: string;
  avgDaysToHire: string;
}

export function mapDashboardStats(s: T.DashboardStats): MappedPlatformStats {
  return {
    totalTalents: s.total_talents ?? 0,
    totalOrgs: s.total_orgs ?? 0,
    activePostings: s.active_postings ?? 0,
    totalApplications: s.total_applications ?? 0,
    totalPaymentsVolume: formatMoney(s.total_payments_volume),
    verificationPending: s.verification_pending ?? 0,
    reportsOpen: s.reports_open ?? 0,
    newTalents30d: s.new_talents_30d ?? 0,
    newOrgs30d: s.new_orgs_30d ?? 0,
    hireRate: `${Math.round(s.hire_rate ?? 0)}%`,
    avgDaysToHire: (s.avg_days_to_hire ?? 0).toFixed(1),
  };
}

export const EMPTY_STATS: MappedPlatformStats = {
  totalTalents: 0, totalOrgs: 0, activePostings: 0, totalApplications: 0,
  totalPaymentsVolume: "SAR 0", verificationPending: 0, reportsOpen: 0,
  newTalents30d: 0, newOrgs30d: 0, hireRate: "0%", avgDaysToHire: "0.0",
};

function shortMonth(month: string): string {
  // API gives "YYYY-MM"; render as "Sep '26" to match the existing chart style.
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const [, year, mo] = m;
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = parseInt(mo, 10) - 1;
  const name = names[idx] ?? mo;
  return `${name} '${year.slice(2)}`;
}

export function mapSignups(rows: { month: string; talent: number; orgs: number }[]) {
  return rows.map((r) => ({ month: shortMonth(r.month), talent: r.talent, orgs: r.orgs }));
}

export function mapRevenue(rows: { month: string; amount: number }[]) {
  return rows.map((r) => ({ month: shortMonth(r.month), amount: r.amount }));
}
